import React, { useRef, useState } from "react";
import { Upload, FileText, Trash2, Sparkles, Paperclip, Truck, FileSearch, Download, ListChecks } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, API_BASE } from "@/lib/api";
import { fileSize, fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, EmptyState, StatusChip, AIBanner } from "@/components/common";
import { cn } from "@/lib/utils";

const KINDS = [
  { key: "customer_doc", label: "Customer document", Icon: FileText },
  { key: "tender", label: "Tender", Icon: FileSearch },
  { key: "supplier_quote", label: "Supplier quote", Icon: Truck },
  { key: "internal", label: "Internal", Icon: Paperclip },
];
const kindMeta = (k) => KINDS.find((x) => x.key === k) || KINDS[3];

export const FilesTab = ({ data, reload, setTab }) => {
  const o = data.opportunity;
  const files = data.files || [];
  const [analyze, setAnalyze] = useState(true);
  const [kind, setKind] = useState("customer_doc");
  const [busy, setBusy] = useState(false);
  const [lastAnalysis, setLastAnalysis] = useState(null);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  const upload = async (fileList) => {
    const arr = Array.from(fileList || []);
    if (!arr.length) return;
    setBusy(true);
    try {
      let added = 0;
      for (const f of arr) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("analyze", analyze ? "true" : "false");
        fd.append("kind", kind);
        const r = await api.post(`/opportunities/${o.id}/files`, fd);
        if (r.data.analysis) {
          added += r.data.analysis.added?.length || 0;
          setLastAnalysis({ file: f.name, ...r.data.analysis });
        }
      }
      toast.success(arr.length === 1 ? `${arr[0].name} uploaded` : `${arr.length} files uploaded`);
      if (analyze && added) toast.success(`${added} new requirement${added === 1 ? "" : "s"} identified`, { description: "Review them in the Requirements tab." });
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (f) => {
    try {
      await api.delete(`/files/${f.id}`);
      toast.success("File removed");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1.6fr_1fr]">
      <div>
        {lastAnalysis && (
          <AIBanner className="mb-5" testId="files-analysis-banner" action={<Button size="sm" variant="outline" className="shrink-0" onClick={() => setTab("requirements")}>Review</Button>}>
            <span className="font-medium">{lastAnalysis.added?.length || 0} new requirement{(lastAnalysis.added?.length || 0) === 1 ? "" : "s"}</span> extracted from <span className="font-medium">{lastAnalysis.file}</span>
            {lastAnalysis.open_questions?.length ? <span className="text-muted-foreground"> · {lastAnalysis.open_questions.length} open question{lastAnalysis.open_questions.length === 1 ? "" : "s"} noted</span> : null}
          </AIBanner>
        )}
        {files.length === 0 ? (
          <EmptyState icon={Paperclip} title="No files yet" description="Drop e-mails, specifications, floor plans or supplier quotes here. The AI reads them and updates the requirements." action={<Button onClick={() => inputRef.current?.click()} className="gap-2 bg-primary text-white hover:bg-[#ff8e76]" data-testid="files-empty-upload-button"><Upload className="h-4 w-4" /> Upload file</Button>} testId="files-empty" />
        ) : (
          <Card className="divide-y" data-testid="files-list">
            {files.map((f) => {
              const m = kindMeta(f.kind);
              const parsedLines = f.parsed?.lines?.length;
              return (
                <div key={f.id} className="group flex items-start gap-4 px-4 py-3.5" data-testid="files-row">
                  <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground"><m.Icon className="h-4 w-4" /></span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="truncate text-sm font-medium" data-testid="files-row-name">{f.name}</span>
                      <StatusChip status={f.kind} label={m.label} />
                      {f.text_chars ? <span className="text-[11px] text-muted-foreground">{Math.round(f.text_chars / 1000)}k characters read</span> : null}
                    </div>
                    <div className="mt-0.5 text-xs text-muted-foreground">{fileSize(f.size)} · {fmtDateTime(f.uploaded_at)}{parsedLines ? ` · ${parsedLines} supplier lines parsed` : ""}</div>
                    {f.text_excerpt ? <p className="mt-1.5 line-clamp-2 text-xs leading-5 text-muted-foreground/90">{f.text_excerpt}</p> : null}
                  </div>
                  <button type="button" title="Remove" onClick={() => remove(f)} className="rounded-md p-1.5 text-muted-foreground opacity-0 transition-opacity hover:bg-muted hover:text-foreground group-hover:opacity-100" data-testid="files-row-delete-button"><Trash2 className="h-4 w-4" /></button>
                </div>
              );
            })}
          </Card>
        )}
      </div>

      <aside className="space-y-4">
        <div
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => { e.preventDefault(); setDragging(false); upload(e.dataTransfer.files); }}
          className={cn("rounded-2xl border-2 border-dashed bg-white p-5 text-center transition-colors", dragging ? "border-primary bg-primary/[0.04]" : "border-border")}
          data-testid="files-dropzone"
        >
          <input ref={inputRef} type="file" multiple accept=".pdf,.docx,.xlsx,.txt,.csv,.md,.eml" className="hidden" onChange={(e) => { upload(e.target.files); e.target.value = ""; }} data-testid="files-upload-input" />
          <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-xl bg-muted text-muted-foreground"><Upload className="h-5 w-5" /></div>
          <div className="mt-3 text-sm font-medium">Drop files here</div>
          <p className="mt-0.5 text-xs text-muted-foreground">PDF, Word, Excel, text. Max a few MB per file.</p>
          <Button variant="outline" size="sm" className="mt-3 gap-1.5" disabled={busy} onClick={() => inputRef.current?.click()} data-testid="files-upload-button">{busy ? "Uploading..." : "Choose files"}</Button>
          <div className="mt-4 space-y-3 text-left">
            <div>
              <div className="mb-1 text-xs text-muted-foreground">File type</div>
              <Select value={kind} onValueChange={setKind}>
                <SelectTrigger className="h-9 rounded-[10px] text-sm" data-testid="files-kind-select"><SelectValue /></SelectTrigger>
                <SelectContent>{KINDS.map((k) => <SelectItem key={k.key} value={k.key}>{k.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <label className="flex items-start gap-2 text-sm">
              <Checkbox checked={analyze} onCheckedChange={(v) => setAnalyze(!!v)} className="mt-0.5" data-testid="files-analyze-checkbox" />
              <span><span className="inline-flex items-center gap-1 font-medium"><Sparkles className="h-3.5 w-3.5 text-primary" /> Extract requirements</span><span className="block text-xs text-muted-foreground">The AI reads the document and adds new requirements, risks and open questions.</span></span>
            </label>
          </div>
        </div>
        <div className="rounded-2xl border bg-white p-4 text-sm hairline">
          <div className="flex items-center gap-2 font-medium"><ListChecks className="h-4 w-4 text-muted-foreground" /> Try it</div>
          <p className="mt-1 text-xs text-muted-foreground">Download the sample tender PDF and drop it here with extraction on - or upload a supplier quote from the Calculation tab.</p>
          <a href={`${API_BASE}/public/sample-tender.pdf`} className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline" data-testid="files-sample-tender-link"><Download className="h-3.5 w-3.5" /> Sample tender (PDF)</a>
        </div>
      </aside>
    </div>
  );
};

export default FilesTab;
